// src/pages/Login.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { sendCode, verifyCode } from '../auth';
import PhoneFieldIntl from '../components/PhoneFieldIntl';
import OTPInput from '../components/OTPInput';
import Background from '../components/Background';
import '../styles/theme.css';
import '../styles/cosmic.css';
import '../styles/Login.css';

function fmtUS(e164) {
	if (!e164) return '';
	const digits = e164.replace(/\D/g, '');
	if (digits.length === 11 && digits[0] === '1') {
		const ten = digits.slice(-10);
		return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
	}
	return e164;
}

export default function Login() {
	const navigate = useNavigate();

	const [phoneE164, setPhoneE164] = useState('');
	const [session, setSession] = useState('');
	const [step, setStep] = useState('phone'); // "phone" | "otp"
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState('');

	// server-driven controls used by OTPInput
	const [cooldownSeconds, setCooldownSeconds] = useState(0);
	const [rateLimited, setRateLimited] = useState(false);

	const validIntl = /^\+\d{7,15}$/.test(phoneE164 || '');

	const handleOtpChange = val => {
		const clean = (val || '').replace(/\D/g, '').slice(0, 6);
		setOtp(clean);
		if (err) setErr('');
	};

	async function requestCode(e) {
		e?.preventDefault();
		setErr('');

		if (!validIntl) {
			setErr('Enter a valid mobile number (E.164).');
			return;
		}

		try {
			setLoading(true);
			const r = await sendCode(phoneE164);
			setSession(r.session || '');
			setCooldownSeconds(r.cooldownSeconds || 0);
			setRateLimited(!!r.rateLimited);
			setOtp('');
			setStep('otp');
		} catch (e2) {
			const msg = e2?.message ? String(e2.message) : 'Could not send code';
			console.error('[login] requestCode error:', msg);
			if (/network error|load failed|failed to fetch|networkerror/i.test(msg)) {
				setErr('Network error — please check your connection and try again.');
			} else {
				setErr(msg || 'Could not send code. Please try again.');
			}
		} finally {
			setLoading(false);
		}
	}

	async function confirmCode(e) {
		e?.preventDefault();
		setErr('');

		// Don't validate length here - button is already disabled if length !== 6
		// This prevents race condition with auto-submit
		const code = otp.replace(/\D/g, '').slice(0, 6);
		if (code.length < 6) {
			// Silent return if called too early (shouldn't happen with button disabled)
			return;
		}

		try {
			setLoading(true);
			const r = await verifyCode({ phoneE164, session, otp });

			if (r.success) {
				navigate('/dashboard', { replace: true });
				return;
			}

			if (r.session) setSession(r.session);
			setErr('Invalid code, please try again.');
		} catch (e2) {
			const msg = e2?.message ? String(e2.message) : 'Verification failed';
			console.error('[login] confirmCode error:', msg);
			if (/network error|load failed|failed to fetch|networkerror/i.test(msg)) {
				setErr('Network error — please check your connection and try again.');
			} else if (/expired|timeout/i.test(msg)) {
				setErr('Code expired. Request a new one.');
			} else if (/Invalid session/i.test(msg)) {
				setErr('Session expired. Request a new code.');
			} else {
				setErr('Invalid code, please try again.');
			}
		} finally {
			setLoading(false);
		}
	}

	return (
		<main className="cosmic-bg">
			<Background variant="auth" />
			<div className="container login-wrap">
				<div
					className="card login-card"
					style={{
						border: '1px solid rgba(255,255,255,0.35)',
						boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
					}}
				>
					<button className="closeX" aria-label="Close" onClick={() => navigate('/')}>
						×
					</button>

					<h1 className="login-title" style={{ textAlign: 'center' }}>
						Log in
					</h1>

					{step === 'phone' ? (
						<form onSubmit={requestCode} className="login-form" style={{ textAlign: 'center' }}>
							<PhoneFieldIntl valueE164={phoneE164} onChangeE164={setPhoneE164} autoFocus disabled={loading} />
							{loading && <div className="sendingBar" aria-hidden="true" />}
							<button className="btn" type="submit" disabled={!validIntl || loading} style={{ marginInline: 'auto' }}>
								{loading ? 'Sending…' : 'Send code'}
							</button>

							<p className="small" style={{ color: '#e5e7eb' }}>
								Not registered?
								<br />
								<u>
									<Link to="/register">Your highest self is waiting for you.</Link>
								</u>
							</p>

							{err && (
								<p className="error" style={{ textAlign: 'center' }}>
									{err}
								</p>
							)}
						</form>
					) : (
						<form onSubmit={confirmCode} className="login-form" style={{ textAlign: 'center' }}>
							<p className="small" style={{ color: '#e5e7eb' }}>
								We texted a code to <span className="mono">{fmtUS(phoneE164)}</span>.
							</p>

							<OTPInput
								value={otp}
								onChange={handleOtpChange}
								onEnter={confirmCode}
								onResend={requestCode}
								cooldownSeconds={cooldownSeconds}
								rateLimited={rateLimited}
								helpText="Enter the 6-digit code we sent via SMS."
								errorText={err}
							/>

							<div className="login-actions" style={{ marginTop: 14, textAlign: 'center' }}>
								<button className="btn" type="submit" disabled={otp.length !== 6 || loading} style={{ marginInline: 'auto' }}>
									{loading ? 'Verifying…' : 'Verify'}
								</button>
							</div>

							<p className="small" style={{ color: '#e5e7eb', textAlign: 'center' }}>
								<button
									className="change-link"
									type="button"
									onClick={() => {
										setStep('phone');
										setOtp('');
										setErr('');
									}}
								>
									<u style={{ color: '#fff' }}>Change number</u>
								</button>
							</p>
						</form>
					)}
				</div>
			</div>
		</main>
	);
}
